import React, { useEffect, useRef } from "react";
import { Editor } from "@toast-ui/react-editor";
import "@toast-ui/editor/dist/toastui-editor.css";
import "./style.css";

interface Props {
  value: string;
  disabled?: boolean;
  placeholder?: string;
  onBlur: (markdown: string) => void;
}

const toolbarItems = [
  ["heading", "bold", "italic"],
  ["ul", "ol", "indent", "outdent"],
  ["table", "link"],
];

const DEFAULT_HEIGHT = 132;

const MarkdownTextArea: React.FC<Props> = ({
  value,
  disabled,
  placeholder,
  onBlur,
}) => {
  const editorRef = useRef<Editor>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const lastHeightRef = useRef<number>(DEFAULT_HEIGHT);
  const didInit = useRef(false);

  useEffect(() => {
    if (!didInit.current) return;
    editorRef.current?.getInstance().setMarkdown(value || "");
  }, [value]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    wrapper.style.height = `${DEFAULT_HEIGHT}px`;
    lastHeightRef.current = DEFAULT_HEIGHT;

    // Custom styling logic to force outer wrapper of editor to be the same height as inner div that is resizable
    const wwContainer = wrapper.querySelector(
      ".toastui-editor-ww-container"
    ) as HTMLElement | null;

    if (!wwContainer) return;

    const observer = new ResizeObserver(() => {
      const rect = wwContainer.getBoundingClientRect();
      const contentHeight = Math.round(rect.height);

      if (contentHeight <= 0) return;

      const nextHeight = contentHeight + 32;

      if (Math.abs(nextHeight - lastHeightRef.current) < 1) return;

      lastHeightRef.current = nextHeight;
      wrapper.style.height = `${nextHeight}px`;
    });

    observer.observe(wwContainer);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={wrapperRef} className="markdownTextArea">
      {/* @ts-ignore */}
      <Editor
        ref={editorRef}
        initialValue={value || ""}
        initialEditType="wysiwyg"
        hideModeSwitch
        usageStatistics={false}
        toolbarItems={toolbarItems}
        height="100%"
        placeholder={placeholder}
        onBlur={() => {
          const instance = editorRef.current?.getInstance();
          if (instance) onBlur(instance.getMarkdown());
        }}
        onLoad={() => (didInit.current = true)}
        //@ts-ignore
        readOnly={disabled}
        autofocus={false}
      />
    </div>
  );
};

export default MarkdownTextArea;
